/**
 * 通用场景工厂：地图编辑器画出来的地图文档（doc）在这里变成真正能进的场景。
 * 结构上与 home 保持一致（同一套系统、同一套 props 落库方式），
 * 差别只是地图来自 doc 的字符画而不是写死的参数。
 */
import { Scene } from '../core/entity.js';
import { Tilemap } from '../core/tilemap.js';
import { TILE_DEFS, MAP_CHARS } from '../render/tiles_art.js';
import { createPlayer } from '../entities/player.js';
import { getProp } from '../data/items.js';
import { canStand, movementSystem } from '../systems/movement.js';
import { poseSystem } from '../systems/pose.js';
import { interactionSystem } from '../systems/interaction.js';
import { timeSystem } from '../systems/time.js';
import { persistSystem } from '../systems/persist.js';
import { cultivateSystem } from '../systems/cultivate.js';
import { Repo } from '../data/schema.js';
import { vehicleSystem } from '../systems/vehicle.js';
import { placeBuildingsForScene } from '../data/place_props.js';

/** 把 doc 里的 props 种子写进数据库，之后玩家可以像在屋里一样挪动、拆除 */
export function loadDocProps(db, scene, doc) {
  const table = db.table('props');
  let rows = table.whereEq('scene', scene.id);
  const houses = placeBuildingsForScene(doc);     // 门户对着的那几栋房子
  if (!rows.length) {
    for (const p of doc.props || []) table.insert({ scene: scene.id, ...p });
    rows = table.whereEq('scene', scene.id);
  }
  // 老存档进来也得瞧见房子：一处门户一行，缺了就补
  if (houses.length && !rows.some((r) => String(r.type || '').startsWith('place_'))) {
    for (const b of houses) table.insert({ scene: scene.id, ...b });
    rows = table.whereEq('scene', scene.id);
  }
  scene.props = rows.map((r) => {
    const def = getProp(r.type);
    return {
      id: r.id,
      type: r.type,
      gx: r.gx, gy: r.gy,
      // 同上：尺寸以器物定义为准，别让表里的默认 1 把多格家具压成一格
      w: def.w || r.w || 1,
      h: def.h || r.h || 1,
      solid: !!def.solid,
      state: { ...(def.state || {}), ...(r.state || {}) },
      def,
    };
  });
}

/** 落点被占（或压根没有存档位置）就退回出生点，出生点也被占就近找空地 */
function findStandSpot(scene, x, y, tm) {
  if (x && y && canStand(scene, x, y)) return { x, y };
  if (canStand(scene, tm.spawn.x, tm.spawn.y)) return { x: tm.spawn.x, y: tm.spawn.y };
  const ts = tm.tileSize;
  for (let r = 1; r <= 12; r += 1) {
    for (const [ox, oy] of [[r, 0], [-r, 0], [0, r], [0, -r], [r, r], [-r, r], [r, -r], [-r, -r]]) {
      const nx = tm.spawn.x + ox * ts;
      const ny = tm.spawn.y + oy * ts;
      if (canStand(scene, nx, ny)) return { x: nx, y: ny };
    }
  }
  return { x: tm.spawn.x, y: tm.spawn.y };
}

/**
 * 由一个地图文档创建场景。
 * @param {object} engine
 * @param {object} doc  { id, name, tileSize, rows, chars, spawn:{gx,gy}, props:[] }
 */
export function createDocScene(engine, doc) {
  const TS = doc.tileSize || 40;
  const scene = new Scene({ id: doc.id, name: doc.name || doc.id });
  const tm = new Tilemap({
    rows: doc.rows,
    chars: { ...MAP_CHARS, ...(doc.chars || {}) },
    tiles: TILE_DEFS,
    tileSize: TS,
  });
  const sp = doc.spawn || { gx: Math.floor(tm.w / 2), gy: Math.floor(tm.h / 2) };
  tm.spawn = { x: sp.gx * TS + TS / 2, y: sp.gy * TS + TS / 2 };
  scene.setTilemap(tm);

  scene.build = (eng) => {
    loadDocProps(eng.db, scene, doc);

    const pdata = Repo.getPlayer(eng.db);
    const spot = findStandSpot(scene, pdata.x, pdata.y, tm);
    const px = spot.x, py = spot.y;
    const player = createPlayer({ x: px, y: py, facing: pdata.facing || 'down' });
    scene.add(player);
    scene.player = player;

    eng.camera.setBounds(tm.pixelW, tm.pixelH);
    eng.camera.follow(player);
    eng.camera.snapTo(px, py);

    scene
      .use(timeSystem)
      .use(poseSystem)
      .use(movementSystem)
      .use(interactionSystem)
      .use(cultivateSystem)     // 吐纳：坐在蒲团 / 香炉上时灵气自涨
      .use(vehicleSystem)        // 车：有自己的转向与碰撞，放在移动之后接管
      .use(persistSystem);
  };

  return scene;
}
